// Is this an address an account can live on? Said before any mail is sent, so
// a typo or a throwaway inbox is caught while the person is still typing.
//
// - Private relays are welcome: Apple's Hide My Email, Firefox Relay,
//   DuckDuckGo, SimpleLogin, addy.io forward to a real inbox and keep working.
// - One-time inboxes are not: a code sent there is read once and the address
//   is gone, so the account (and its one trial, 044) can't be recovered and is
//   usually a second trial in disguise. The list is DISPOSABLE_DOMAINS below;
//   the database refuses the same domains when the account is created
//   (048_email_domains.sql), and emailCheck.test.ts keeps the two in step.
// - A common provider misspelt ("gmial.com") is offered back corrected.

/** Inboxes that exist for a few minutes. A subdomain of one counts too. */
export const DISPOSABLE_DOMAINS = [
  '10minutemail.com', '10minutemail.net', '20minutemail.com', 'anonbox.net', 'byom.de', 'cool.fr.nf',
  'deadaddress.com', 'discard.email', 'discardmail.com', 'dispostable.com', 'dropmail.me', 'easytrashmail.com',
  'email-fake.com', 'emailfake.com', 'emailondeck.com', 'emailtemporanea.net', 'emltmp.com', 'fakeinbox.com',
  'fakemail.net', 'fakemailgenerator.com', 'fexbox.org', 'fexpost.com', 'generator.email', 'getairmail.com',
  'getnada.com', 'grr.la', 'guerrillamail.biz', 'guerrillamail.com', 'guerrillamail.de', 'guerrillamail.net',
  'guerrillamail.org', 'guerrillamailblock.com', 'harakirimail.com', 'inboxbear.com', 'inboxkitten.com',
  'incognitomail.org', 'jetable.fr.nf', 'jetable.org', 'linshiyouxiang.net', 'luxusmail.org', 'mail-temp.com',
  'mail.tm', 'mail7.io', 'mailcatch.com', 'maildrop.cc', 'mailforspam.com', 'mailhazard.com', 'mailinator.com',
  'mailinator.net', 'mailinator2.com', 'mailnesia.com', 'mailnull.com', 'mailpoof.com', 'mailsac.com',
  'mailtemp.info', 'mailto.plus', 'mintemail.com', 'minuteinbox.com', 'moakt.com', 'mohmal.com', 'mvrht.com',
  'mytemp.email', 'mytrashmail.com', 'nada.email', 'nowmymail.com', 'one-time.email', 'onetimeemail.net',
  'owlymail.com', 'pokemail.net', 'sharklasers.com', 'smailpro.com', 'spam4.me', 'spambox.us', 'spamdecoy.net',
  'spamex.com', 'spamgourmet.com', 'spamherelots.com', 'temp-mail.io', 'temp-mail.org', 'temp.email',
  'tempail.com', 'tempinbox.com', 'tempm.com', 'tempmail.com', 'tempmail.dev', 'tempmail.net',
  'tempmail.ninja', 'tempmail.plus', 'tempmailaddress.com', 'tempmailo.com', 'temporary-mail.net',
  'tempr.email', 'throwawaymail.com', 'tmail.ws', 'tmpeml.com', 'tmpmail.net', 'tmpmail.org', 'trash-mail.com',
  'trashmail.com', 'trashmail.de', 'trashmail.io', 'trashmail.net', 'trbvm.com', 'vomoto.com',
  'wegwerfmail.de', 'wegwerfmail.net', 'yopmail.com', 'yopmail.fr', 'yopmail.net', 'zetmail.com',
] as const

/** Relay services that forward to a real inbox: never refused, whatever lists elsewhere say. */
export const PRIVATE_RELAYS = ['privaterelay.appleid.com', 'mozmail.com', 'duck.com', 'simplelogin.com', 'aleeas.com', 'slmail.me', 'addy.io', 'anonaddy.com', 'anonaddy.me'] as const

/** Misspellings of the providers most people use, and what they meant. */
const TYPOS: Record<string, string> = {
  'gmial.com': 'gmail.com', 'gmai.com': 'gmail.com', 'gamil.com': 'gmail.com', 'gmal.com': 'gmail.com', 'gmail.co': 'gmail.com',
  'gmail.con': 'gmail.com', 'gmail.se': 'gmail.com', 'gnail.com': 'gmail.com', 'gmaill.com': 'gmail.com',
  'hotmial.com': 'hotmail.com', 'hotmal.com': 'hotmail.com', 'hotmail.con': 'hotmail.com', 'hotmai.com': 'hotmail.com',
  'hotmial.se': 'hotmail.se', 'hotnail.se': 'hotmail.se', 'hotmal.se': 'hotmail.se',
  'outlok.com': 'outlook.com', 'outloo.com': 'outlook.com', 'outlook.con': 'outlook.com',
  'iclod.com': 'icloud.com', 'icoud.com': 'icloud.com', 'icloud.con': 'icloud.com', 'iclould.com': 'icloud.com',
  'yahooo.com': 'yahoo.com', 'yaho.com': 'yahoo.com', 'yahoo.con': 'yahoo.com',
  'live.con': 'live.com', 'live.sw': 'live.se', 'telia.co': 'telia.com', 'bredband.nte': 'bredband.net',
}

const SHAPE = /^[^\s@]{1,64}@([a-z0-9-]{1,63}\.)+[a-z]{2,24}$/

export type EmailCheck =
  | { ok: true; email: string; suggestion?: string }
  | { ok: false; error: string }

export const EMAIL_ERRORS = {
  empty: 'Enter your email.',
  shape: 'That doesn’t look like an email address. Check it for typos.',
  disposable: 'One-time inboxes can’t hold an account. Use an email you keep. A private relay like Hide My Email works.',
} as const

/** The domain an address belongs to, or null when it has no @. */
export function domainOf(email: string): string | null {
  const at = email.lastIndexOf('@')
  return at === -1 ? null : email.slice(at + 1).toLowerCase()
}

/** A throwaway domain, or a subdomain of one ("x.mailinator.com"). */
export function isDisposable(domain: string): boolean {
  const d = domain.toLowerCase().replace(/\.$/, '')
  if ((PRIVATE_RELAYS as readonly string[]).includes(d)) return false
  return DISPOSABLE_DOMAINS.some(x => d === x || d.endsWith(`.${x}`))
}

export function checkEmail(raw: string): EmailCheck {
  const email = raw.trim().replace(/\.$/, '')
  if (!email) return { ok: false, error: EMAIL_ERRORS.empty }
  const lower = email.toLowerCase()
  if (!SHAPE.test(lower) || lower.includes('..')) return { ok: false, error: EMAIL_ERRORS.shape }
  const domain = domainOf(lower)!
  if (isDisposable(domain)) return { ok: false, error: EMAIL_ERRORS.disposable }
  const meant = TYPOS[domain]
  return meant ? { ok: true, email, suggestion: `${email.slice(0, email.lastIndexOf('@'))}@${meant}` } : { ok: true, email }
}
